/**
 * CameraRigSubsystem - Camera rig execution for player runtime
 * 
 * Automatically updates CameraRig components each frame,
 * handling target following, damping, noise, and all Cinemachine-like behaviors.
 */

import {
  Subsystem,
  type SubsystemConfig,
  SubsystemPriority,
  type Level,
  cameraRigSystem,
  updateCameraRigs,
  type CameraRigOutput,
} from '@engine/core';
import type { LevelSubsystem } from './LevelSubsystem';

/**
 * Camera rig execution subsystem
 */
export class CameraRigSubsystem extends Subsystem {
  readonly config: SubsystemConfig = {
    name: 'cameraRig',
    priority: SubsystemPriority.Presentation - 10, // Before rendering, after physics
    optional: true,
    dependencies: ['level'],
  };
  
  private enabled = true;
  private _lastOutput: CameraRigOutput | null = null;
  
  // ============================================================================
  // LIFECYCLE
  // ============================================================================
  
  async onInitialize(): Promise<void> {
    console.log('[CameraRigSubsystem] Initialized');
  }
  
  onStart(): void {
    // Reset camera rig state when game starts
    cameraRigSystem.reset();
  }
  
  onUpdate(deltaTime: number): void {
    if (!this.enabled) return;
    
    const level = this.getCurrentLevel();
    if (!level) return;
    
    // Update camera rigs and apply to transforms
    this._lastOutput = updateCameraRigs(level, deltaTime);
  }
  
  onShutdown(): void {
    cameraRigSystem.reset();
  }
  
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  
  /** Enable/disable camera rig updates */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }
  
  /** Check if camera rig system is enabled */
  isEnabled(): boolean {
    return this.enabled;
  }
  
  /** Get the last camera output (useful for rendering) */
  get lastOutput(): CameraRigOutput | null {
    return this._lastOutput;
  }
  
  /** Reset all rig states (call on level change) */
  reset(): void {
    cameraRigSystem.reset();
    this._lastOutput = null;
  }
  
  // ============================================================================
  // HELPERS
  // ============================================================================
  
  private getCurrentLevel(): Level | null {
    // Try to get level from LevelSubsystem
    const levelSub = this.getSubsystem<LevelSubsystem>('level');
    return levelSub?.currentLevel ?? null;
  }
}
